import React, { useState, useEffect, Fragment, useRef } from "react";
import { useTable, useBlockLayout } from "react-table";
import { useOutletContext} from "react-router-dom";
import { FixedSizeList as List } from "react-window";
import { formatValue } from "./Utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import Select, { components } from "react-select";
import { getCFOSelectProps } from "./form-components/cfoSelectClassNames.js";

const API_URL = process.env.REACT_APP_API_URL;

function Table({ columns, data }) {
	const defaultColumn = React.useMemo(() => ({ width: 150 }), []);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
		initialState,
	} = useTable({ columns, data, defaultColumn }, useBlockLayout);

	initialState.hiddenColumns = ["cost_allocation_id", "cost_allocation_id"];

	const RenderRow = React.useCallback(
		({ index, style }) => {
			const row = rows[index];
			prepareRow(row);
			return (
				<div
					{...row.getRowProps({
						style,
					})}
					className="tr"
				>
					{row.cells.map((cell) => {
						return (
							<div
								{...cell.getCellProps()}
								className="td truncate pl-4 pt-4 pb-6 text-sm"
							>
								{cell.render("Cell")}
							</div>
						);
					})}
				</div>
			);
		},
		[prepareRow, rows]
	);

	return (
		<div {...getTableProps()} className="table w-full cursor-default">
			<div className="bg-surface-2 py-4 pl-4 text-text-2">
				{headerGroups.map((headerGroup) => (
					<div {...headerGroup.getHeaderGroupProps()} className="tr">
						{headerGroup.headers.map((column) => (
							<div
								{...column.getHeaderProps()}
								className="th uppercase text-sm"
							>
								{column.render("Header")}
							</div>
						))}
					</div>
				))}
			</div>

			<div {...getTableBodyProps()} className="">
				<List height={500} itemCount={rows.length} itemSize={60} width="100%">
					{RenderRow}
				</List>
			</div>
		</div>
	);
}

const UnallocatedCostsDataTable = () => {
	const [columns, setColumns] = useState([]);
	const [data, setData] = useState([]);
	const dataRef = useRef();
	const { jwt } = useOutletContext();

	dataRef.current = data;

	var organisations = [];

	function getRequestOptions() {
		if (jwt === "") return;
		
		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + jwt)
	
		let method = "GET";
	
		let requestOptions = {
			method: method,
			headers: headers,
		};
	
		return requestOptions;
	}

	const setValue = (value, id) => {
		var tmpData = dataRef.current.map((row) => {
			if (row.cost_allocation_id === id) {
				row.organisation_id = value.value;
				row.organisation_name = value.text;
			}

			return row;
		});
		setData(tmpData);
	};

	
	
	const Option = (props) => {
		// display a checkmark after the selected item
		if (String(props.data.text) === String(props.selectProps.value.value)) {
			return (
				<div>
					<span className="mt-2 right-4 absolute">
						<FontAwesomeIcon
							icon={icon({ name: "check", style: "light" })}
							className="ml-2 text-right"
						/>
					</span>
					<components.Option {...props} />
				</div>
			);
		} else {
			return (
				<div>
					<components.Option {...props} />
				</div>
			);
		}
	};

	const columnDefs = [
		{
			Header: "ID",
			accessor: "cost_allocation_id",
		},
		{
			Header: "Vendor",
			accessor: "vendor_name",
			Cell: (row) => <div className="text-text-2 mt-2">{row.value}</div>,
			width: 200,
		},
		{
			Header: "Project",
			accessor: "project_name",
			Cell: (row) => <div className="text-text mt-2">{row.value}</div>,
			width: 250,
		},
		{
			Header: "Service",
			accessor: "service_name",
			Cell: (row) => <div className="text-text mt-2">{row.value}</div>,
			width: 300,
		},
		{
			Header: (row) => <div className="text-left ml-5">Organisation</div>,
			accessor: "organisation_name",
			Cell: (row) => (
				<Select
					{...getCFOSelectProps()}
					options={organisations}
					components={{
						Option,
						IndicatorSeparator: () => null,
					}}
					menuPortalTarget={document.body}
					defaultValue={{ text: row.value, value: row.value }}
					isSearchable={false}
					formatOptionLabel={(e) => {
						return (
							<div>
								{Number(e.value) === -1 && (
									<div
										className="px-3 py-2 text-text-2 border-t border-border"
										style={{ display: "flex" }}
									>
										<span className="ml-0">{e.text}</span>
									</div>
								)}
								{Number(e.value) !== -1 && (
									<div className="px-3 py-2" style={{ display: "flex" }}>
										<span className="ml-0 text-text">{e.text}</span>
									</div>
								)}
							</div>
						);
					}}
					onChange={(e) => setValue(e, row.row.original.cost_allocation_id)}
					styles={{
						control: (baseStyles) => ({
							...baseStyles,
							border: 0,
							boxShadow: "none",
						}),
					}}
				/>
			),
			width: 200,
		},
		{
			accessor: "costs",
			Header: (row) => <div className="text-right mr-4">Cost</div>,
			Cell: (row) => (
				<div className="text-right text-text font-semibold mt-2">
					{formatValue(row.value, "currency", false)}
				</div>
			),
			width: 200,
		},
	];

	function retrieveOrganisations() {
		fetch(API_URL + "/get-organisations", getRequestOptions())
			.then((response) => {
				if (response.status !== "200") {
					// let err = Error;
					// err.message = "Invalid response code: " + response.status;
					// setError(err);
				}
				return response.json();
			})
			.then((json) => {
				organisations = json.Organisations.map((organisation) => {
					return {
						value: organisation.id,
						text: organisation.name,
						description: organisation.description,
					};
				});
				organisations.push({
					value: "-1",
					text: "Not specified",
					description: "Not specified",
				});
			});
	}

	function retrieveData() {
		let url = API_URL + "/get-unallocated-costs";

		fetch(url)
			.then((response) => {
				if (response.status !== "200") {
					let err = Error;
					err.message = "Invalid response code: " + response.status;
					// setError(err);
				}
				return response.json();
			})
			.then((json) => {
				setData(json.UnallocatedCosts);
				setColumns(columnDefs);
			});
	}

	useEffect(() => {
		retrieveOrganisations();
		retrieveData();
	// Mount-only fetch. The fetchers are re-created on every render, so listing them
	// here would refetch on every render.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	
	return (
		<Fragment>
			<div className="h-64 ml-10 font-semibold text-3xl text-text-2"></div>
			<Table columns={columns} data={data} />
		</Fragment>
	);
};

export default UnallocatedCostsDataTable;
